/* global URL_MENU */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import paulinaAvatar from '@assets/images/paulina-avatar.jpg';
import { useAuth } from '@context/AuthContext';
import { useNotificationContext } from '@context/NotificationsContext';
import { usePaulina } from '@components/AgentChat/PaulinaProvider';
import { PaulinaNudgeTeaserMobile } from '@components/AgentChat/PaulinaNudgeTeaserMobile';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useCanSwitchBranch } from '@utils/hooks/useCanSwitchBranch';
import { MobileNavDrawerV4 } from './MobileNavDrawerV4';
import { V4 } from './tokens';
import BranchPicker from './BranchPicker';

const ACCOUNT_PATHS = ['perfil-social', 'mi-cuenta', 'ayuda', 'soporte', 'actualizaciones', 'sugerencias'];

// Tiempo que el teaser proactivo permanece visible antes de auto-ocultarse dejando el dot.
const TEASER_AUTO_HIDE_MS = 8000;

// Pulso sutil del dot del avatar cuando hay nudge pendiente — llama la atención sin ser
// intrusivo. Solo corre bajo `prefers-reduced-motion: no-preference`; con motion reducido el
// dot queda estático. Inyección idempotente en <head> (mismo patrón que las superficies de Paulina).
const PULSE_KF_ID = 'plick-paulina-dot-pulse-kf';
if (typeof document !== 'undefined' && !document.getElementById(PULSE_KF_ID)) {
  const st = document.createElement('style');
  st.id = PULSE_KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-dot-pulse{0%,100%{box-shadow:0 0 0 0 rgba(31,196,154,.55)}70%{box-shadow:0 0 0 6px rgba(31,196,154,0)}}' +
    '.plick-paulina-dot-pulse{animation:plick-paulina-dot-pulse 1800ms ease-out infinite}}';
  document.head.appendChild(st);
}

export const MobileTopBarV4: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { commerce } = useGetCommerce();
  const { canSwitch, branches, activeBranchName, switchTo, isRoot } = useCanSwitchBranch();
  const { notifications } = useNotificationContext();
  const hasNotifications = notifications.length > 0;
  // Entrada de Paulina (Opción A) en el header móvil. El estado open es compartido
  // con el bottom-sheet vía PaulinaProvider. Se suprime cuando hay pedido entrante
  // (hayPedido) — lo operativo gana — y cuando el agente no está habilitado.
  const paulina = usePaulina();
  const showPaulina = Boolean(paulina?.enabled) && !paulina?.hayPedido;
  const nudge = paulina?.nudge ?? null;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);

  // Teaser proactivo del nudge (mini-bubble anclado al avatar). Aparece SOLO cuando llega un
  // nudge nuevo; se auto-oculta a ~8 s (o al descartarlo / abrir el chat) dejando el dot verde
  // como indicador persistente. `teaserNudgeId` recuerda qué nudge ya disparó el teaser, para
  // no re-abrirlo en cada render una vez auto-oculto. El dot (paulina?.nudge) persiste igual.
  const [teaserNudgeId, setTeaserNudgeId] = useState<string | null>(null);
  const [teaserVisible, setTeaserVisible] = useState(false);

  // Auto-aparición + auto-ocultado del teaser. Al llegar un nudge cuyo id aún no disparó el
  // teaser, lo mostramos y programamos su auto-ocultado. El gate showPaulina (sin pedido) ya
  // suprime toda la entrada de Paulina; aquí además exigimos que no haya sheet abierto.
  useEffect(() => {
    if (!showPaulina || !nudge || paulina?.open) return;
    if (nudge.id === teaserNudgeId) return; // ya se mostró para este nudge
    setTeaserNudgeId(nudge.id);
    setTeaserVisible(true);
    const t = window.setTimeout(() => setTeaserVisible(false), TEASER_AUTO_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [showPaulina, nudge, paulina?.open, teaserNudgeId]);

  const teaserOn = showPaulina && Boolean(nudge) && teaserVisible && !paulina?.open;

  // Tap en el teaser o el avatar → abre el sheet (con el nudge completo dentro) y oculta el teaser.
  const openPaulina = () => {
    setTeaserVisible(false);
    paulina?.toggleOpen();
  };

  const isAccountLevel = ACCOUNT_PATHS.some((p) => location.pathname.includes(p));

  const handleNavigate = (path: string) => {
    navigate(`/${path}`);
    setDrawerOpen(false);
  };

  const handleGoToMenu = () => {
    if (commerce.id_path) window.open(`${URL_MENU}/${commerce.id_path}`, '_blank');
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  return (
    <>
      <header
        style={{
          height: 52,
          background: V4.primary,
          color: V4.white,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px 0 12px',
          gap: 8,
          flexShrink: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        {/* Hamburger */}
        <button
          onClick={() => setDrawerOpen(true)}
          style={{
            background: 'transparent',
            border: 0,
            color: V4.white,
            cursor: 'pointer',
            padding: 6,
          }}
          aria-label="Abrir menú"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Branch pill */}
        <button
          onClick={() => canSwitch && !isAccountLevel && setBranchSheetOpen(true)}
          disabled={isAccountLevel || !canSwitch}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'rgba(255,255,255,.12)',
            color: V4.white,
            border: 0,
            borderRadius: 9999,
            padding: '6px 10px 6px 6px',
            cursor: !canSwitch || isAccountLevel ? 'default' : 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 600,
            opacity: isAccountLevel ? 0.55 : 1,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: V4.secondary,
              color: V4.white,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 22s8-5 8-12a8 8 0 1 0-16 0c0 7 8 12 8 12z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span
            style={{
              flex: 1,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isAccountLevel ? 'Cuenta' : activeBranchName}
          </span>
          {canSwitch && !isAccountLevel && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        {/* Paulina — avatar de entrada (Opción A). Mismo patrón de botón-icono
            transparente; abre el bottom-sheet (PaulinaSheet) vía el estado compartido.
            Proactividad en móvil: cuando hay un nudge pendiente se enciende el dot verde
            (con pulso sutil) y, al llegar, aparece un mini-teaser anclado al avatar con el
            TÍTULO del nudge (PaulinaNudgeTeaserMobile); el nudge completo se ve al abrir el
            sheet. El speech-bubble completo flotante sigue siendo exclusivo de desktop. */}
        {showPaulina && (
          <button
            onClick={openPaulina}
            style={{
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              padding: 4,
              display: 'inline-flex',
              flexShrink: 0,
              position: 'relative',
            }}
            aria-label="Abrir chat con Paulina"
          >
            <img
              src={paulinaAvatar}
              alt=""
              aria-hidden="true"
              style={{
                width: 29,
                height: 29,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${V4.secondary}`,
              }}
            />
            {nudge && (
              <span
                aria-hidden="true"
                // El pulso solo corre con un nudge pendiente y se desactiva con motion reducido
                // (la clase queda inerte: el @keyframes vive bajo prefers-reduced-motion:no-preference).
                className="plick-paulina-dot-pulse"
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: V4.accentMint,
                  border: `1.5px solid ${V4.primary}`,
                }}
              />
            )}
          </button>
        )}

        {/* Bell */}
        <button
          style={{
            background: 'transparent',
            border: 0,
            color: V4.white,
            cursor: 'pointer',
            padding: 6,
            position: 'relative',
          }}
          aria-label="Notificaciones"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.7 21a1.95 1.95 0 0 1-3.4 0"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {hasNotifications && (
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: V4.danger,
                border: `1.5px solid ${V4.primary}`,
              }}
            />
          )}
        </button>

        {/* Teaser proactivo del nudge: mini-bubble anclado al avatar con SOLO el título.
            Tap → abre el sheet (nudge completo dentro). Descartable con la X. Cuelga del header
            (este header es fixed/relative para el absolute del teaser). */}
        {teaserOn && nudge && (
          <PaulinaNudgeTeaserMobile nudge={nudge} onOpen={openPaulina} onDismiss={() => setTeaserVisible(false)} />
        )}
      </header>

      {/* Branch bottom sheet */}
      {branchSheetOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 250,
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <div
            onClick={() => setBranchSheetOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(37,55,70,.45)' }}
          />
          <div
            style={{
              position: 'relative',
              width: '100%',
              background: V4.white,
              borderRadius: '16px 16px 0 0',
              padding: '14px 16px 20px',
              animation: 'plick-drawer-up 240ms cubic-bezier(.2,.8,.2,1)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                background: V4.grayLight,
                borderRadius: 2,
                margin: '0 auto 14px',
              }}
            />
            <div style={{ fontSize: 15, fontWeight: 700, color: V4.primary, marginBottom: 10 }}>Cambiar sucursal</div>
            <BranchPicker
              branches={branches}
              current={activeBranchName}
              variant="light"
              autoFocus
              maxHeight="46vh"
              onPick={(n) => {
                switchTo(n);
                setBranchSheetOpen(false);
              }}
            />
          </div>
          <style>
            {'@keyframes plick-drawer-up { from { transform: translateY(100%); } to { transform: translateY(0); } }'}
          </style>
        </div>
      )}

      <MobileNavDrawerV4
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeBranch={activeBranchName}
        branches={canSwitch ? branches.map((b) => b.nombre) : []}
        isAccountLevel={isAccountLevel}
        canChangeBranch={canSwitch}
        showCreateBranch={isRoot}
        onBranchSelect={switchTo}
        activePathname={location.pathname}
        onNavigate={handleNavigate}
        onGoToMenu={handleGoToMenu}
        onLogout={handleLogout}
        commerceName={commerce.nombre ?? ''}
        userName={user.username}
        commerceLogotipo={commerce.logotipo ?? undefined}
      />
    </>
  );
};
